import { useOktaAuth } from '@okta/okta-react';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../components/general/Spinner';
import { GCS_CONFIGS } from '../../config/gcs/gcsConfigs';
import { PENDING_SIGN_IN_GCS_ID_KEY } from '../../constants/auth';
import { useGcsStore } from '../../store/useGcsStore';
import { AppError, ErrorDomain, ErrorCode, normalizeError } from '../../types/errors/errors';
import { AsyncAction } from '../../types/general/asyncAction';

export function OktaCallbackPage() {
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const hasRun = useRef(false);
  const [signInAction, setSignInAction] = useState<AsyncAction>(AsyncAction.loading());
  const signInWithOkta = useGcsStore((state) => state.signInWithOkta);
  const connectAll = useGcsStore((state) => state.connectAll);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const signOutFromOkta = async (postLogoutRedirectUri: string, state?: string): Promise<boolean> => {
      try {
        // Sign out from Okta after authentication to improve security.
        // This reduces risk on shared devices and allows others to login.
        await oktaAuth.signOut({ postLogoutRedirectUri, state });
      } catch (error) {
        Sentry.captureException(error);
      }

      return window.location.pathname !== '/okta-callback';
    };

    const onSignInSuccess = async () => {
      connectAll();
      const redirected = await signOutFromOkta(`${window.location.origin}/`);
      if (!redirected) {
        navigate('/', { replace: true });
      }
    };

    const onSignInFailure = async (error: unknown) => {
      const normalizedError = normalizeError(error);
      const serializedError = {
        domain: normalizedError.domain,
        code: normalizedError.code
      };
      const redirected = await signOutFromOkta(`${window.location.origin}/sign-in`, JSON.stringify(serializedError));
      if (!redirected) {
        const errorState = encodeURIComponent(JSON.stringify(serializedError));
        navigate(`/sign-in?state=${errorState}`, { replace: true });
      }
    };

    const signIn = async () => {
      setSignInAction(AsyncAction.loading());

      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code') || undefined;

        if (!code) {
          throw new AppError({
            domain: ErrorDomain.AUTH,
            code: ErrorCode.AUTH_OKTA_CALLBACK_MISSING_CODE
          });
        }

        const redirectUri = oktaAuth.options.redirectUri;
        if (!redirectUri) {
          throw new AppError({
            domain: ErrorDomain.AUTH,
            code: ErrorCode.AUTH_OKTA_CALLBACK_MISSING_REDIRECT_URI
          });
        }

        const pendingGcsId = localStorage.getItem(PENDING_SIGN_IN_GCS_ID_KEY);
        localStorage.removeItem(PENDING_SIGN_IN_GCS_ID_KEY);

        if (!pendingGcsId) {
          throw new AppError({
            domain: ErrorDomain.AUTH,
            code: ErrorCode.AUTH_OKTA_CALLBACK_MISSING_PENDING_GCS_ID
          });
        }

        const gcsConfig = GCS_CONFIGS.find((config) => config.id === pendingGcsId);
        if (!gcsConfig) {
          throw new AppError({
            domain: ErrorDomain.AUTH,
            code: ErrorCode.AUTH_OKTA_CALLBACK_GCS_CONFIG_NOT_FOUND
          });
        }

        await signInWithOkta(gcsConfig.id, code, redirectUri);
        setSignInAction(AsyncAction.success());
        await onSignInSuccess();
      } catch (error) {
        setSignInAction(AsyncAction.failure(error));
        await onSignInFailure(error);
      }
    };

    void signIn();
  }, [connectAll, navigate, oktaAuth, signInWithOkta]);

  if (signInAction.isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          width: '100vw'
        }}
      >
        <Spinner size={36} />
      </div>
    );
  }

  return null;
}
